import React, {Component} from 'react';
import h from 'react-hyperscript';
import './BarCharts.scss';
import {
  createFirstTouchListener,
  unMountD3,
  d3WrapText,
  getChartItemSlug,
  getPercentValue} from 'Src/utils/utils';

import * as d3 from 'd3';
import GraphTooltip from 'Components/GraphTooltip/GraphTooltip';

class BarChartVertical extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.margin = 20;
    this.offsetTop = 10;
    this.barOffset = 6;
    this.parentMargin = 30;
    this.offsetRightDefault = 40;
    this.barWidth = this.props.barWidth;
    this.height = this.props.size[1];
    this.parentId = this.props.parentId;
    this.ticks = [0, .2, .4, .6, .8, 1];
    this.formatPercent = d3.format('.0%');
    this.handleClick = this.handleClick.bind(this);
    this.handleKeypress = this.handleKeypress.bind(this);
    this.toggleLabel = this.toggleLabel.bind(this);
    this.toggleBar = this.toggleBar.bind(this);
    this.tooltipRefs = {};

    this.width = this.props.size[0] - 50;
    this.barContainerWidth = this.calcBarContainerWidth;

    this.state = {
      userTouch: false,
      activeStep: null,
    };

    this.scale = d3.scaleLinear()
        .domain([0, d3.max(this.props.data, (d) => 1)])
        .range([this.height - this.margin, 0]);


    this.makeYGridLines = () => {
      return d3.axisRight(this.scale).tickValues(this.ticks);
    };

    this.createBarChartVertical = this.createBarChartVertical.bind(this);
    this.barChartRef = React.createRef();

    createFirstTouchListener(()=> this.setState({userTouch: true}));
  }

  componentDidMount() {
    this._isMounted = true;
    this.checkActiveStep();
    this.initGraph(true);

    const setRef = this.props.setRef;

    if (setRef) {
      setRef(this.barChartRef);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const updateGraph = this.props.hasIcons ?
    prevProps.size[0] !== this.props.size[0] :
    prevProps.size !== this.props.size;

    if (updateGraph) {
      this.removeGraphElements();
      this.initGraph(true, {reset: true});
    }

    if (this.props.activeStep !== prevProps.activeStep) {
      this.checkActiveStep();
    }

    if (!prevState.userTouch && this.state.userTouch) {
      this.removeGraphElements();
      this.initGraph(true);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.setState({
      width: this.props.size[0],
      barContainer: this.props.size[0] / this.props.data.length,
    });
    this.removeEventListeners();
    unMountD3(this.node, d3);
  }

  removeGraphElements() {
    this.removeGraphContent();

    if (this.props.hasIcons) {
      this.removeIcons();
    }
  }

  removeGraphContent() {
    d3.select(this.node).selectAll('*').remove();
  }

  removeIcons() {
    d3.selectAll(`#bar-chart__labels__${this.props.id}`)
        .selectAll('div').remove();
  }

  checkActiveStep() {
    if (this.props.activeStep) {
      this.setActiveStep();
    }
  }

  setActiveStep() {
    this.setState({
      activeStep: this.props.activeStep,
    }, () => {
      if (this.state.activeStep) {
        const chart = d3.select(this.node);

        chart.selectAll('.graph__label--value')
            .style('font-weight', (d) => {
              if (d.name.toLowerCase() === this.state.activeStep) {
                return '700';
              }
            });
        chart.selectAll('.bar-rect')
            .attr('class', (d) => {
              const labelName = d.name.toLowerCase();
              const id = `bar-rect-${labelName}`;

              if (labelName === this.state.activeStep) {
                return this.getBarRectClass(id, true); ;
              } else {
                return this.getBarRectClass(id, false); ;
              }
            });


        this.props.data.forEach((prod, i) => {
          if (this.props.hasIconsText) {
            const labelName = prod.name.toLowerCase();
            const isActive = this.props.activeStep === labelName;
            const labelContainer = d3.select(`.label-${labelName}`);
            const base = 'label__container label-';
            const flex = 'flex flex--center';
            const hasClick = this.props.onLabelClick ? ' has-click' : '';
            const active = isActive ? ' active': '';
            const inactive = isActive ? ' inactive' : '';
            const classes = `${base}${labelName} ${flex}${hasClick}${inactive}`;
            labelContainer.attr('class', `${classes}`);

            labelContainer.select('.label__image')
                .attr('class', `label__image${isActive ? ' active' : ''}`);


            labelContainer.select('.label__image_text')
                .attr('class', `label__image_text eyebrow${active}`)
                .text(prod.name);
          }
        });
      }
    });
  }

  setActiveRect(slug) {
    const activePrev = this.state.activeRect;

    this.setState({
      activeRect: slug,
    }, () => {
      const {activeRect} = this.state;

      if (activePrev) {
        const tooltipPrev = this.tooltipRefs[activePrev];
        if (tooltipPrev) {
          tooltipPrev.makeInactive();
        }
      }

      if (activeRect) {
        const rect = this.barChartRef.current.querySelector(
            `[data-rect="${activeRect}"]`
        );
        const tooltip = this.tooltipRefs[activeRect];
        const {x, y, width} = rect.getBoundingClientRect();
        const {
          x: x2,
          y: y2,
          width: widthParent,
          height: heightParent} = this.node.getBoundingClientRect();

        if (tooltip) {
          const xPos = (x + (width / 2)) - x2;
          const yPos = y - y2;
          tooltip.setPosition(xPos, yPos, {
            width: widthParent,
            height: heightParent,
          });
          tooltip.makeActive();
        }
      }
    });
  }

  setTooltipRef(id, ref) {
    this.tooltipRefs[id] = ref;
  }

  initGraph(init, options = {}) {
    const {reset} = options;

    if (reset) {
      this.width = this.props.size[0] - 40;
      this.barContainerWidth = this.calcBarContainerWidth;
    }

    if (init) {
      this.createBarChartVertical();
    } else {
      this.updateBarChart();
    }
  }

  get calcBarContainerWidth() {
    const offset = (this.props.offsetRight || this.offsetRightDefault);
    return (this.props.size[0] - offset) / this.props.data.length;
  }

  removeEventListeners() {
    if (this.props.onLabelClick) {
      const query = `#${this.props.id} .bar.has-click`;
      const elementsWithListener = document.querySelectorAll(query);
      elementsWithListener.forEach((item) => {
        item.removeEventListener('click', this.handleClick);
        item.removeEventListener('touch', this.handleClick);
        item.removeEventListener('keyup', this.handleKeypress);
      });
    }
  }

  updateBarChart() {
    const yAxis = d3.axisRight()
        .scale(this.scale)
        .tickValues(this.ticks)
        .tickFormat(this.formatPercent);

    const svg = d3.select(this.node);

    svg.selectAll('.grid')
        .attr('transform',
            `translate(${this.width}, ${this.offsetTop})`)
        .call(
            this.makeYGridLines()
                .tickSize(-(this.width))
                .tickFormat(''));

    svg.selectAll('.y-axis')
        .call(yAxis)
        .attr('transform',
            `translate(${this.width + 100}, ${this.offsetTop})`);

    svg.selectAll('.bar')
        .attr('width', this.barContainerWidth)
        .attr('transform', (d, i) => {
          const xPos = this.barContainerWidth * i;
          return `translate(${xPos - 6}, ${this.offsetTop})`;
        });

    const {dataPrev} = this.props;

    svg.selectAll('.bar-rect')
        .attr('width', this.barWidth)
        .attr('y', (d, i, array) => {
          let value = d.value;
          const {isPrev} = array[i].dataset;
          if (isPrev) {
            const prevMatch = dataPrev.find((item) => item.name === d.name);
            if (prevMatch) {
              value = prevMatch.value;
            } else {
              value = 0;
            }
          }
          return this.scale(value);
        })
        .attr('height', (d, i, array) => {
          let value = d.value;
          const {isPrev} = array[i].dataset;
          if (isPrev) {
            const prevMatch = dataPrev.find((item) => item.name === d.name);
            if (prevMatch) {
              value = prevMatch.value;
            } else {
              value = 0;
            }
          }
          return this.scale(0) - this.scale(value);
        });
  }

  getSlug(name, isPrev) {
    const parts = [name.toLowerCase().split(' ').join('-')];
    if (isPrev) {
      parts.push('prev');
    }
    return parts.join('-');
  }

  barDataGenerator(bars, isPrev) {
    const {dataPrev} = this.props;
    let xPos = this.barContainerWidth / 2;
    if (dataPrev) {
      xPos = xPos - ((this.barWidth / 2) * (isPrev ? 1 : -1));
    }

    return () => {
      bars.append('rect')
          .attr('width', this.barWidth)
          .attr('tabindex', 0)
          .attr('y', (d) => {
            let value = d.value;
            if (isPrev) {
              const prevMatch = this.props.dataPrev.find((item) =>
                item.name === d.name
              );
              if (prevMatch) {
                value = prevMatch.value;
              } else {
                value = 0;
              }
            }
            return this.scale(value);
          })
          .attr('x', xPos)
          .attr('height', (d) => {
            let value = d.value;
            if (isPrev) {
              const prevMatch = this.props.dataPrev.find((item) =>
                item.name === d.name
              );
              if (prevMatch) {
                value = prevMatch.value;
              } else {
                value = 0;
              }
            }
            return this.scale(0) - this.scale(value);
          })
          .attr('class', (d) => {
            const labelName = d.name.toLowerCase();
            const id = `bar-rect-${labelName}`;

            if (labelName === this.state.activeStep || !this.onLabelClick) {
              return this.getBarRectClass(id, true);
            } else {
              return this.getBarRectClass(id, false);
            }
          })
          .attr('data-is-prev', (d) => {
            return isPrev;
          })
          .attr('data-rect', (d) => {
            return getChartItemSlug(d.name, isPrev);
          })
          .on('mouseover', (d) => this.setActiveRect(
              getChartItemSlug(d.name, isPrev)
          ))
          .on('mouseout', (d) => this.setActiveRect(null));
    };
  }

  createBarChartVertical() {
    const yAxis = d3.axisRight()
        .scale(this.scale)
        .tickValues(this.ticks)
        .tickFormat(this.formatPercent);

    const svg = d3.select(this.node);
    const onLabelClick = this.props.onLabelClick;

    svg.append('g')
        .attr('class', 'grid')
        .attr('transform',
            `translate(${this.width}, ${this.offsetTop})`)
        .call(
            this.makeYGridLines()
                .tickSize(-(this.width))
                .tickFormat(''));

    svg.append('g')
        .call(yAxis)
        .attr('class', 'y-axis')
        .attr('transform',
            `translate(${this.width}, ${this.offsetTop})`);

    const bars = svg.selectAll('.bar')
        .data(this.props.data)
        .enter()
        .append('g')
        .attr('width', this.barContainerWidth)
        .attr('role', 'button')
        .attr('class', `bar${onLabelClick ? ' has-click' : ''} tab-focus`)
        .style('position', 'relative')
        .attr('transform', (d, i) => {
          const xPos = this.barContainerWidth * i;
          return `translate(${xPos - this.barOffset}, ${this.offsetTop})`;
        });

    const createBarData = this.barDataGenerator(bars);
    createBarData();
    const {dataPrev} = this.props;

    if (dataPrev) {
      const createBarDataPrev = this.barDataGenerator(bars, true);
      createBarDataPrev();
    }

    if (this.props.hasIcons) {
      this.createIconLabels();
    } else {
      bars.append('text')
          .attr('y', this.height)
          .attr('x', (this.barContainerWidth/2) + this.barOffset )
          .attr('dy', '.35em')
          .attr('class', (d) => {
            const labelName = d.name.toLowerCase();
            const id = `label-${labelName}`;

            if (labelName === this.state.activeStep) {
              return `label--active graph__label--value footnote ${id}`;
            } else {
              return `graph__label--value footnote ${id}`;
            }
          })
          .attr('fill', '#5f6368')
          .attr('text-anchor', 'middle')
          .text((d) => d.name);

      d3.selectAll('.graph__label--value').call(d3WrapText, d3);
    }

    if (onLabelClick) {
      bars
          .attr('tabindex', 0)
          .attr('aria-label', ({name}) => {
            return `View recommended resources for ${name}`;
          });

      bars.on('click', this.handleClick);
      bars.on('keyup', this.handleKeypress);
      bars.on('mouseout', (d) => this.toggleLabel(d, {isActive: false}));
      bars.on('mouseover', (d) => this.toggleLabel(d, {isActive: true}));
      this.checkActiveStep();
    }
  }

  createIconLabels() {
    this.props.data.forEach((prod, i) => {
      const parent = d3.selectAll(`#bar-chart__labels__${this.props.id}`);
      const xPos = this.barContainerWidth * i;
      const labelName = prod.name.toLowerCase();
      const isActive = this.props.activeStep === labelName;
      const base = 'label__container label-';
      const flex = ' flex flex--center';
      const hasClick = this.props.onLabelClick ? ' has-click' : '';
      const active = isActive ? ' active' : '';

      const label = parent.append('div')
          .attr('class', `${base}${labelName}${flex}${hasClick}${active}`)
          .attr('data-name', labelName)
          .attr('role', 'button')
          .attr('tabindex', '0')
          .style('width', `${this.barContainerWidth}px`)
          .style('position', 'absolute')
          .style('top', `${this.height}px`)
          .style('left', `${xPos}px`);

      label.append('img')
          .attr('class', `label__image${isActive ? ' active' : ''}`)
          .attr('src', prod.icon.standard)
          .attr('srcset', `${prod.icon.standard} 1x, ${prod.icon.retina} 2x`)
          .attr('width', '30px')
          .style('display', 'block')
          .style('margin', '0 auto');

      if (this.props.hasIconsText) {
        label.append('p')
            .attr('class', `label__image_text eyebrow${active}`)
            .text(labelName);
      }

      if (this.props.onLabelClick) {
        if (this.state.userTouch) {
          label.on('click', (d, i, el) => {
            this.handleClick(d, i, el);
            this.toggleBar(labelName, {
              isActive: true,
            });
          });
        } else {
          label.on('click', this.handleClick);
          label.on('keyup', this.handleKeypress);
          label.on('focus', () => this.toggleBar(labelName, {
            isActive: true,
          }));
          label.on('blur', () => this.toggleBar(labelName, {
            isActive: false,
          }));
          label.on('mouseover', () => this.toggleBar(labelName, {
            isActive: true,
          }));
          label.on('mouseout', () => this.toggleBar(labelName, {
            isActive: false,
          }));
        }
      }
    });
  }

  stepName(d, el) {
    return d ? d.name.toLowerCase() : el[0].dataset.name.toLowerCase();
  }

  handleClick(d, i, el) {
    this.props.onLabelClick(this.stepName(d, el), this.props.element);
  }

  handleKeypress(d, i, el) {
    const {code} = d3.event;

    if (code === 'Enter' || code === 'Space') {
      this.props.onLabelClick(this.stepName(d, el), this.props.element);
    }
  }

  labelContainerClass(labelName, {isActive}) {
    return `label__container label-${labelName} flex flex--center${
      this.props.onLabelClick ? ' has-click' : ''
    }${
      isActive ? ' active' : ''
    }`;
  }

  toggleLabel(d, {isActive}) {
    const labelName = d.name.toLowerCase();

    const labelContainer = d3.select(`.label-${labelName}`);

    labelContainer
        .attr(this.labelContainerClass(labelName, {isActive: true}));

    if (this.props.hasIcons) {
      labelContainer.select('.label__image')
          .attr('class', `label__image${isActive ? ' active' : ''}`);

      if (this.props.hasIconsText) {
        const active = isActive ? ' active': '';
        labelContainer.select('.label__image_text')
            .attr('class', `label__image_text eyebrow${active}`);
      }
    }
  }

  toggleBar(labelName, {isActive}) {
    const id = `bar-rect-${labelName}`;
    const bar = d3.select(`.${id}`);

    bar
        .attr('class', (d) => {
          const currentLabelName = d.name.toLowerCase();
          if (isActive || currentLabelName === this.state.activeStep) {
            this.setActiveRect(getChartItemSlug(d.name));
            return this.getBarRectClass(id, true);
          } else {
            this.setActiveRect();
            return this.getBarRectClass(id, false);
          }
        });
  }

  getBarRectClass(id, active) {
    return active ? `${id} bar-rect has-active-step` : `${id} bar-rect`;
  }

  createChartKey(name, isPrev) {
    const prev = isPrev ? ' bar-chart__key--prev' : '';
    return (
      h('p', {
        className: `bar-chart__key ${name}${prev}`,
      }, [
        h('span', {
          className: 'bar-chart__key__color',
        }),
        name,
      ])
    );
  }

  createLegend() {
    const {chartName, chartNamePrev} = this.props;

    const chartKeys = [this.createChartKey(chartName)];
    if (chartNamePrev) {
      chartKeys.unshift(this.createChartKey(chartNamePrev, true));
    }
    return (
      h('div', {
        className: 'bar-chart__legend flex--center',
      }, chartKeys)
    );
  }

  render() {
    const tooltips = [];
    if (!this.props.hideTooltips) {
      tooltips.push(...this.props.data.map((item) => this.renderTooltip(item)));

      if (this.props.dataPrev) {
        tooltips.push(...this.props.dataPrev.map((item) =>
          this.renderTooltip(item, true)
        ));
      }
    }

    const chartComponents = [
      h('div', {
        className: 'chart bar-chart__container',
        id: this.props.id,
        ref: this.barChartRef,
      }, [
        h('div', {
          className: 'bar-chart__chart',
          style: {
            width: this.props.size[0],
            height: this.height + this.parentMargin,
          },
        }, [
          h('svg', {
            ref: (node) => this.node = node,
            width: this.props.size[0],
            height: this.height + this.parentMargin,
          }),
          h('div', {
            className: 'bar-chart__tooltips',
          }, tooltips),
          h('div', {
            id: `bar-chart__labels__${this.props.id}`,
            className: 'bar-chart__labels',
          }),
        ]
        ),
      ]),
    ];

    if (this.props.chartName) {
      chartComponents.push(this.createLegend());
    }

    return (
      h('div', null, chartComponents)
    );
  }

  renderTooltip(item, isPrev) {
    const {name: title, value: val, icon} = item;
    const value = getPercentValue(val) + '%';

    return h(GraphTooltip, {
      title, value, icon,
      id: getChartItemSlug(title, isPrev),
      setRef: this.setTooltipRef.bind(this),
    });
  }
}

export default BarChartVertical;
